<!-- Main overview screen -->
<template>
  <div style="min-height: 105vh" class="row">
    <!-- Loading cube -->
    <div
      v-if="!loaded"
      class="col-xs-12 row middle-xs center-xs"
      style="height: 100vh"
    >
      <div style="width: fit-content">
        <loading-cube />
      </div>
    </div>

    <transition name="routing" mode="out-in">
      <div v-if="loaded" class="col-xs-12 row">
        <!-- Header -->
        <div class="col-xs-12">
          <app-header />
          <div style="height: var(--header-height)" />
        </div>

        <!-- Filter menu button (mobile) -->
        <button
          class="filter-menu-button row middle-xs center-xs"
          @click="filterMenuVisible = !filterMenuVisible"
        >
          <icon class="col" icon="filter" />
        </button>

        <!-- Filter menu -->
        <transition
          enter-active-class="animated slideInRight faster"
          leave-active-class="animated slideOutRight faster"
        >
          <filter-menu
            v-if="filterMenuVisible"
            :totalCount="allDesigns.length"
            :filteredCount="filteredDesigns.length"
            :toggleFunction="
              () => {
                this.filterMenuVisible = false;
              }
            "
          />
        </transition>

        <!-- Charts and tables for filtering -->
        <div class="col-md-10">
          <div class="row">
            <div
              v-if="$store.state.filters.enabled.includes('month')"
              class="col-md-11 col-xs-11 chart-double"
            >
              <div class="chart-title">Months</div>
              <month-chart
                :chartData="Object.values(byMonth)"
                :chartLabels="Object.keys(byMonth)"
              />
            </div>
            <div
              v-if="$store.state.filters.enabled.includes('category')"
              class="col-md-5 col-xs-11 chart-single"
            >
              <div class="chart-title">Categories</div>
              <category-chart :chartData="byCategory" />
            </div>
            <div
              v-if="$store.state.filters.enabled.includes('goal')"
              class="col-md-5 col-xs-11 chart-single"
            >
              <div class="chart-title">Goals</div>
              <goal-chart :chartData="byGoal" />
            </div>
            <div
              v-if="$store.state.filters.enabled.includes('continent')"
              class="col-md-5 col-xs-11 chart-single"
            >
              <div class="chart-title">Continents</div>
              <continent-chart :chartData="byContinent" />
            </div>
            <div
              v-if="$store.state.filters.enabled.includes('year')"
              class="col-md-5 col-xs-11 chart-single"
            >
              <div class="chart-title">Award Years</div>
              <year-chart :chartData="byYear" />
            </div>
            <div
              v-if="$store.state.filters.enabled.includes('region')"
              class="col-md-5 col-xs-11 chart-single"
            >
              <div class="chart-title">Regions of Use</div>
              <region-chart :chartData="byRegion" />
            </div>
            <div
              v-if="$store.state.filters.enabled.includes('country')"
              class="col-md-5 col-xs-11 chart-single"
            >
              <div class="chart-title">Countries</div>
              <vue-table
                class="table-countries"
                :localData="byCountry"
                :fields="[
                  { name: 'name', sortField: 'name' },
                  { name: 'value', sortField: 'value', title: 'Count' },
                ]"
                :onCellClick="onCountryClick"
                :sortOrder="[{ field: 'value', direction: 'desc' }]"
              />
            </div>
            <div
              v-if="$store.state.filters.enabled.includes('keyword')"
              class="col-md-5 col-xs-11 chart-single"
            >
              <div class="chart-title">Keywords</div>
              <vue-table
                class="table-keywords"
                :localData="byKeyword"
                :fields="[
                  { name: 'name', sortField: 'name' },
                  { name: 'value', sortField: 'value', title: 'Count' },
                ]"
                :onCellClick="onKeywordClick"
                :sortOrder="[{ field: 'value', direction: 'desc' }]"
              />
            </div>
            <div class="col-md-11 col-xs-11 chart-double">
              <div class="chart-title">Designs</div>
              <vue-table
                class="table-designs"
                :localData="filteredDesigns"
                :fields="
                  $mq === 'lg'
                    ? [
                        { name: 'name', sortField: 'name' },
                        { name: 'short_description', title: 'Description' },
                        {
                          name: 'country.name',
                          sortField: 'country.name',
                          title: 'Country',
                        },
                        {
                          name: 'award_year',
                          sortField: 'award_year',
                          title: 'Award Year',
                        },
                        {
                          name: 'keywords',
                          formatter: $options.filters.keywords,
                        },
                      ]
                    : [
                        { name: 'id', sortField: 'id' },
                        { name: 'name', sortField: 'name' },
                        { name: 'short_description', title: 'Description' },
                      ]
                "
                :onCellClick="onDesignClick"
              />
            </div>
            <div v-if="$mq === 'sm'" class="col-xs-11" style="height: 15vw" />
          </div>
        </div>

        <!-- Footer -->
        <div class="col-xs-12">
          <app-footer />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Chart from "chart.js";

export default {
  name: "Overview",
  data() {
    return {
      filterMenuVisible: false,
      loaded: false,
      allDesigns: null,
      byCategory: [],
      byGoal: [],
      byCountry: [],
      byContinent: [],
      byRegion: [],
      byYear: [],
      byKeyword: [],
      byMonth: {},
      indexUsers: [],
    };
  },
  computed: {
    // Gets all filtered designs and groups them for charts and tables
    filteredDesigns() {
      let filtered = this.allDesigns.filter(this.filter);
      this.groupDesigns(filtered);
      return filtered;
    },
  },
  methods: {
    // Routes to design article
    onDesignClick(payload) {
      if (payload.field.name == "name") {
        let routeData = this.$router.resolve({
          name: "design",
          params: { id: payload.data.id },
        });
        window.open(routeData.href, "_blank");
      }
    },
    // Adds country to the filter array
    onCountryClick(payload) {
      let id = payload.data.id;
      let index = this.$store.state.filters.country.indexOf(id);
      if (index > -1) this.$store.state.filters.country.splice(index, 1);
      else this.$store.state.filters.country.push(id);
    },
    // Adds keyword to the filter array
    onKeywordClick(payload) {
      let id = payload.data.id;
      let index = this.$store.state.filters.keyword.indexOf(id);
      if (index > -1) {
        this.$store.state.filters.keyword.splice(index, 1);
        this.$store.state.filters.keywordName.splice(index, 1);
      } else {
        this.$store.state.filters.keyword.push(id);
        this.$store.state.filters.keywordName.push(payload.data.name);
      }
    },
    // Gets an array of Index user IDs, stores in idb
    getIndexUsers() {
      this.$idbGet("indexUsers")
        .then((val) => {
          if (val === undefined || !this.$cookies.isKey("designsStored")) {
            this.$http
              .get(this.$store.state.apiURL + "users/indexers")
              .then((response) => {
                console.log("fetched new index users...");
                this.indexUsers = response.body.users.map((user) => user.id);
                this.$idbSet("indexUsers", this.indexUsers)
                  .then(() => {
                    console.log("index users saved.");
                  })
                  .catch((err) => {
                    console.log("Error while saving index users", err);
                  });
              });
          } else {
            console.log("stored index users fetched.");
            this.indexUsers = val;
          }
        })
        .catch((e) => {
          console.log("failed to get index users, fetching from api");
          this.$http
            .get(this.$store.state.apiURL + "users/indexers")
            .then((response) => {
              console.log("new index users fetched.");
              this.indexUsers = response.body.users.map((user) => user.id);
            });
        });
    },
    // Gets the settings from idb
    getSettings() {
      this.$idbGet("settings").then((val) => {
        if (val === undefined) {
          console.log("stored settings not found, using default settings.");
        } else {
          console.log("stored settings fetched.");
          this.$store.state.settings = val;
        }
      });
    },
    // Gets all designs and stores in idb, uses a cookie to fetch new designs after 1 day
    getDesigns(url) {
      this.$idbGet("designs")
        .then((val) => {
          if (val === undefined || !this.$cookies.isKey("designsStored")) {
            this.$http.get(this.$store.state.apiURL + url).then((response) => {
              console.log("new designs fetched, saving...");
              this.allDesigns = response.body;
              this.loaded = true;
              this.$idbSet("designs", response.body)
                .then(() => {
                  console.log("new designs saved.");
                  this.$cookies.set("designsStored", true);
                })
                .catch((err) => {
                  console.log("Error while saving designs", err);
                });
            });
          } else {
            console.log("stored designs fetched.");
            this.allDesigns = val;
            this.loaded = true;
          }
        })
        .catch((e) => {
          console.log("failed to get designs, fetching from api");
          this.$http.get(this.$store.state.apiURL + url).then((response) => {
            console.log("new designs fetched.");
            this.allDesigns = response.body;
          });
        });
    },
    // Filters each design based on filters. If invalid on any step, returns false
    filter(design) {
      let filters = this.$store.state.filters;
      let extra = this.$store.state.filters.extra;
      let mode = this.$store.state.filters.mode;
      let competitions = extra.filter((f) => f.startsWith("comp-"));

      // Category
      if (filters.category.length != 0) {
        if (
          mode.category == "or" &&
          !filters.category.includes(design.category_id)
        )
          return false;
        if (
          mode.category == "nor" &&
          filters.category.includes(design.category_id)
        )
          return false;
      }

      // Goal
      if (filters.goal.length != 0) {
        if (
          mode.goal == "or" &&
          !design.challenges.some((challenge) =>
            filters.goal.includes(challenge.id)
          )
        )
          return false;
        if (
          mode.goal == "nor" &&
          design.challenges.some((challenge) =>
            filters.goal.includes(challenge.id)
          )
        )
          return false;
        if (
          mode.goal == "and" &&
          !filters.goal.every((goal) =>
            design.challenges.some((challenge) => goal == challenge.id)
          )
        )
          return false;
      }

      // Country
      if (filters.country.length != 0) {
        if (
          mode.country == "or" &&
          !filters.country.includes(design.country_id)
        )
          return false;
        if (
          mode.country == "nor" &&
          filters.country.includes(design.country_id)
        )
          return false;
      }

      // Year
      if (filters.year.length != 0) {
        if (mode.year == "or" && !filters.year.includes(design.award_year))
          return false;
        if (mode.year == "nor" && filters.year.includes(design.award_year))
          return false;
      }

      // Continent
      if (filters.continent.length != 0) {
        if (
          mode.continent == "or" &&
          !filters.continent.includes(design.country.continent_id)
        )
          return false;
        if (
          mode.continent == "nor" &&
          filters.continent.includes(design.country.continent_id)
        )
          return false;
      }

      // Keyword
      if (filters.keyword.length != 0) {
        if (
          mode.keyword == "or" &&
          !design.keywords.some((keyword) =>
            filters.keyword.includes(keyword.id)
          )
        )
          return false;
        if (
          mode.keyword == "nor" &&
          design.keywords.some((keyword) =>
            filters.keyword.includes(keyword.id)
          )
        )
          return false;
        if (
          mode.keyword == "and" &&
          !filters.keyword.every((keyword) =>
            design.keywords.some((k) => keyword == k.id)
          )
        )
          return false;
      }

      // Region
      if (filters.region.length != 0) {
        if (
          mode.region == "or" &&
          !design.regions.some((region) => filters.region.includes(region.id))
        )
          return false;
        if (
          mode.region == "nor" &&
          design.regions.some((region) => filters.region.includes(region.id))
        )
          return false;
        if (
          mode.region == "and" &&
          !filters.region.every((region) =>
            design.regions.some((r) => region == r.id)
          )
        )
          return false;
      }

      // Month
      if (filters.month.length != 0) {
        let date = new Date(
          design.created_at.toString().split(" ")[0].replace(/-/g, "/")
        );
        let month =
          date.getFullYear() +
          "-" +
          (date.getMonth() + 1).toString().padStart(2, "0");
        if (mode.month == "or" && !filters.month.includes(month)) return false;
        if (mode.month == "nor" && filters.month.includes(month)) return false;
      }

      // From Index
      // if (extra.includes("index")) {
      //   if (!this.indexUsers.includes(design.user_id)) return false;
      // }

      // Status
      if (extra.includes("finalist") || extra.includes("winner")) {
        if (!extra.includes(design.status)) return false;
      }

      // Competitions
      if (competitions.length != 0) {
        if (!competitions.includes(`comp-${design.competition_id}`))
          return false;
      }

      // Search (array)
      if (filters.search.length != 0) {
        let combinedString = (
          design.id.toString() +
          " " +
          design.name +
          " " +
          design.short_description +
          " " +
          design.long_description +
          " " +
          design.how_description +
          " " +
          design.why_description +
          " " +
          design.dtil_description +
          " " +
          design.country.name +
          " " +
          design.keywords.map((k) => k.name).toString() +
          " " +
          design.credits
        ).toLowerCase();
        if (
          mode.search == "or" &&
          !filters.search.some((searchTerm) =>
            combinedString.includes(searchTerm)
          )
        )
          return false;
        if (
          mode.search == "nor" &&
          filters.search.some((searchTerm) =>
            combinedString.includes(searchTerm)
          )
        )
          return false;
        if (
          mode.search == "and" &&
          !filters.search.every((searchTerm) =>
            combinedString.includes(searchTerm)
          )
        )
          return false;
      }

      this.adjustCase(design);

      return true;
    },
    // Groups designs based on attributes for all charts and tables.
    groupDesigns(designs) {
      let categories = [0, 0, 0, 0, 0];
      let goals = [
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
      ];
      let countries = [];
      let continents = [0, 0, 0, 0, 0, 0];
      let regions = [0, 0, 0, 0, 0, 0, 0];
      let years = [0, 0, 0, 0, 0];
      let keywords = [];
      let months = {};
      let orderedMonths = {};

      for (let design of designs) {
        // by category
        categories[design.category_id - 1]++;

        // by goal (challenge)
        if (this.$store.state.filters.enabled.includes("goal"))
          for (let goal of design.challenges) {
            goals[goal.id - 1]++;
          }

        // by country
        if (!countries.some((country) => country.id === design.country.id))
          countries.push({
            id: design.country.id,
            value: 1,
            name: design.country.name,
          });
        else
          countries.find((country) => country.id === design.country.id).value++;

        // by continent of origin
        continents[design.country.continent_id - 1]++;

        // by region of use
        if (this.$store.state.filters.enabled.includes("region"))
          for (let region of design.regions) {
            regions[region.id - 1]++;
          }

        // by award year
        years[(design.award_year - 2015) / 2]++;

        // by keyword
        if (this.$store.state.filters.enabled.includes("keyword"))
          for (let keyword of design.keywords) {
            if (!keywords.some((k) => k.id === keyword.id))
              keywords.push({ id: keyword.id, value: 1, name: keyword.name });
            else keywords.find((k) => k.id === keyword.id).value++;
          }

        // by month
        if (this.$store.state.filters.enabled.includes("month")) {
          let date = new Date(
            design.created_at.toString().split(" ")[0].replace(/-/g, "/")
          );
          let month =
            date.getFullYear() +
            "-" +
            (date.getMonth() + 1).toString().padStart(2, "0");
          if (months.hasOwnProperty(month)) months[month]++;
          else months[month] = 1;
        }
      }

      // Fills empty months with 0 values
      // By default, the fetched months are ordered in reverse
      if (
        this.$store.state.filters.enabled.includes("month") &&
        Object.keys(months).length > 1
      ) {
        let first =
          Object.keys(months)[Object.keys(months).length - 1].split("-"); // [firstYear, firstMonth]
        let last = Object.keys(months)[0].split("-"); // [lastYear, lastMonth]
        for (let i = first[0]; i <= last[0]; i++) {
          // loops through months, adjusts if it's the first year or last year
          for (
            let j = i == first[0] ? first[1] : 1;
            j <= (i == last[0] ? last[1] : 12);
            j++
          ) {
            if (!months.hasOwnProperty(i + "-" + j.toString().padStart(2, "0")))
              months[i + "-" + j.toString().padStart(2, "0")] = 0;
          }
        }
      }

      // sorting months
      if (this.$store.state.filters.enabled.includes("month")) {
        Object.keys(months)
          .sort()
          .forEach((key) => {
            orderedMonths[key] = months[key];
          });
      }

      this.byCategory = categories;
      this.byGoal = goals;
      this.byCountry = countries;
      this.byContinent = continents;
      this.byRegion = regions;
      this.byYear = years;
      this.byKeyword = keywords;
      this.byMonth = orderedMonths;
    },
    adjustCase(design) {
      design.name = design.name.toLowerCase();
      design.short_description = design.short_description.toLowerCase();
    },
    adjustFontSize() {
      if (this.$mq === "sm") {
        Chart.defaults.global.defaultFontSize = 10;
      } else {
        Chart.defaults.global.defaultFontSize = 12;
      }
    },
    setToDefaultFilters() {
      this.$store.state.filters = JSON.parse(
        JSON.stringify(this.$store.state.defaultFilters)
      );
    },
  },
  created() {
    // Try to get user data with a token
    // If everything is fine, initialise designs
    // If token was invalid, redirect to login screen
    this.$store.dispatch("user").then(
      (response) => {
        this.setToDefaultFilters();
        this.getIndexUsers();
        this.getSettings();
        this.getDesigns("designs/all");
      },
      (error) => {
        console.log("token invalid");
        this.$router.push("/login");
      }
    );
  },
  mounted() {
    this.adjustFontSize();
    if (this.$mq === "lg") this.filterMenuVisible = true;
  },
};
</script>

<style scoped>
.chart-title {
  margin: 40px 0 20px 0;
  font-family: IndexText-Bold;
}
.chart-double {
  margin-left: 4.166666%;
  margin-right: 4.166666%;
}
.chart-single {
  margin-left: 4.166666%;
  margin-right: 4.166666%;
}
@media only screen and (max-width: 767px) {
  .filter-menu-button {
    height: 10vw !important;
    width: 10vw !important;
    margin: 3.5vw !important;
    font-size: var(--vw50px) !important;
    z-index: 2 !important;
  }
}
@media only screen and (min-width: 768px) {
  .filter-menu-button {
    margin: 1vw !important;
  }
}
.filter-menu-button {
  height: 2.5vw;
  width: 2.5vw;
  font-size: var(--vw28px);
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 0;
  background-color: var(--red);
  border-width: 0;
  color: var(--white);
  cursor: pointer;
  padding: 0;
}
</style>